import { ArrowRight } from 'lucide-react';
import { buttonVariants } from './ui/Button';
import { HeroAskDemo } from './HeroAskDemo';
import { HeroPulse } from './HeroPulse';
import { useT } from '@/i18n/provider';
import { trackEvent } from '@/lib/analytics';

export function HeroSection() {
  const t = useT();

  return (
    <section className="relative overflow-hidden bg-white dark:bg-gray-950">
      {/* Subtle dot grid: data points, faded towards the edges */}
      <div
        className="absolute inset-0 pointer-events-none [background-image:radial-gradient(circle,rgb(12_17_29/0.07)_1px,transparent_1px)] dark:[background-image:radial-gradient(circle,rgb(255_255_255/0.06)_1px,transparent_1px)] [background-size:24px_24px] [mask-image:radial-gradient(ellipse_70%_65%_at_50%_35%,black,transparent)]"
        aria-hidden="true"
      />

      {/* Pulse wallpaper, borrowed from the demo film */}
      <HeroPulse />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-16 sm:pt-36 sm:pb-24 lg:pt-44 lg:pb-32">
        <div className="grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] gap-14 lg:gap-16 items-center">
          {/* Copy */}
          <div>
            <p className="eyebrow animate-slide-up">{t('hero.eyebrow')}</p>

            <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl xl:text-[4.25rem] xl:leading-[1.05] font-bold tracking-tight text-gray-950 dark:text-white text-balance animate-slide-up">
              {t('hero.title')}
            </h1>

            <p className="mt-6 text-lg sm:text-xl text-gray-600 dark:text-gray-400 max-w-xl text-balance animate-slide-up" style={{ animationDelay: '0.1s' }}>
              {t('hero.subtitle')}
            </p>

            {/* Scale as proof of quality and an explicit invitation in one
                breath. Without this line the page reads as enterprise software
                and a thirty-person company counts itself out. */}
            <p className="mt-4 text-base text-gray-500 dark:text-gray-400 max-w-xl text-balance animate-slide-up" style={{ animationDelay: '0.15s' }}>
              {t('hero.inclusion')}
            </p>

            {/* One CTA. The second button used to point at the film below as
                "the 30-second demo"; it is an impression, not a demo, and the
                section speaks for itself when the visitor scrolls to it. */}
            <div className="mt-9 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 animate-slide-up" style={{ animationDelay: '0.2s' }}>
              <a
                href="https://app.logpulse.io/sign-up"
                className={buttonVariants({ variant: 'default', size: 'xl' })}
                onClick={() => trackEvent('hero_cta_click', { cta: 'primary' })}
              >
                {t('hero.cta')}
                <ArrowRight className="h-5 w-5 ml-1" />
              </a>
            </div>

            <p className="mt-6 text-sm text-gray-500 dark:text-gray-500 animate-slide-up" style={{ animationDelay: '0.25s' }}>
              {t('hero.trust')}
            </p>
          </div>

          {/* Plain-language product demo: a question in, an answer out */}
          <div className="animate-slide-up lg:pl-2" style={{ animationDelay: '0.3s' }}>
            <HeroAskDemo />
          </div>
        </div>
      </div>
    </section>
  );
}
